"use client"

import * as React from "react"
import {
  UserCheck,
  Plus,
  Trash2,
  Pencil,
  Shield,
  Loader2,
  AlertTriangle,
  FolderOpen,
  LogOut,
  Layers,
  Inbox,
  Mail,
  Clock
} from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Account, UserProfile } from "@/types/workspace"
import { MemberWorkspaceCell } from "@/components/settings/tabs/MemberWorkspaceCell"
import { toast } from "@/lib/toast"

export interface UsersSettingsTabProps {
  usersList: UserProfile[]
  user?: UserProfile | null
  profileRole?: string
  currentAccount: Account | null
  activeAccountId: string
  installedApps?: any[]
  handleInviteUser: (email: string, name: string, role: string, workspaces?: Array<{ workspaceId: string; role: string }>) => Promise<any> | void
  handleUpdateAccountMember?: (userId: string, data: { name?: string; role?: string; status?: string; customRoleId?: string | null }) => Promise<any> | void
  handleUpdateUserRole?: (userId: string, role: string) => void
  handleRemoveUser: (userId: string) => Promise<any> | void
  t: Record<string, string>
}

export function UsersSettingsTab({
  usersList,
  user,
  profileRole,
  currentAccount,
  activeAccountId,
  installedApps = [],
  handleInviteUser,
  handleUpdateAccountMember,
  handleUpdateUserRole,
  handleRemoveUser,
  t,
}: UsersSettingsTabProps) {
  const isAdmin = profileRole === "admin" || profileRole === "owner"
  const [memberFilter, setMemberFilter] = React.useState<"all" | "active" | "inactive" | "pending">("active")
  const [pendingInvites, setPendingInvites] = React.useState<any[]>([])

  // Invite Modal State
  const [showInviteModal, setShowInviteModal] = React.useState(false)
  const [inviteEmail, setInviteEmail] = React.useState("")
  const [inviteRole, setInviteRole] = React.useState("member")
  const [inviteWorkspaces, setInviteWorkspaces] = React.useState<Record<string, { enabled: boolean; role: string }>>({})
  const [isInviting, setIsInviting] = React.useState(false)

  // Initialize workspace selection whenever modal is opened or workspaces change
  React.useEffect(() => {
    if (showInviteModal && currentAccount?.workspaces) {
      const initial: Record<string, { enabled: boolean; role: string }> = {}
      currentAccount.workspaces.forEach((ws) => {
        initial[ws.id] = { enabled: true, role: "member" }
      })
      setInviteWorkspaces(initial)
    }
  }, [showInviteModal, currentAccount])

  // Edit Member Modal State
  const [editingMember, setEditingMember] = React.useState<any>(null)
  const [editMemberRole, setEditMemberRole] = React.useState("member")
  const [editMemberStatus, setEditMemberStatus] = React.useState<"active" | "inactive">("active")
  const [isSavingMember, setIsSavingMember] = React.useState(false)

  // Delete Member Confirmation Modal State
  const [memberToDelete, setMemberToDelete] = React.useState<any | null>(null)
  const [isDeletingMember, setIsDeletingMember] = React.useState(false)

  // Leave Company Modal State
  const [memberToLeave, setMemberToLeave] = React.useState<any | null>(null)
  const [isLeavingMember, setIsLeavingMember] = React.useState(false)

  // User Workspaces Map
  const [userWorkspacesMap, setUserWorkspacesMap] = React.useState<Record<string, string[]>>({})

  const fetchUserWorkspaces = React.useCallback(async (userId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          query: `
            query GetUserWorkspaceMemberships($userId: ID!) {
              userWorkspaceMemberships(userId: $userId) {
                workspaceId
              }
            }
          `,
          variables: { userId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.userWorkspaceMemberships) {
        setUserWorkspacesMap(prev => ({
          ...prev,
          [userId]: result.data.userWorkspaceMemberships.map((m: any) => m.workspaceId)
        }))
      }
    } catch (e) {
      console.error(e)
    }
  }, [])

  // Effective users list (with fallback to current user if empty)
  const effectiveUsersList = React.useMemo(() => {
    if (usersList && usersList.length > 0) return usersList
    if (user) {
      return [
        {
          id: user.id,
          userId: user.id,
          name: user.name || "Usuário Principal",
          email: user.email || "",
          avatarUrl: user.avatarUrl || null,
          role: profileRole || "owner",
          accountRole: profileRole || "owner",
          companyRole: profileRole || "owner",
          status: "active",
        }
      ]
    }
    return []
  }, [usersList, user, profileRole])

  // Filtered members list
  const filteredUsersList = React.useMemo(() => {
    if (memberFilter === "all") return effectiveUsersList
    if (memberFilter === "active") return effectiveUsersList.filter((u: any) => (u.status || "active") !== "inactive")
    if (memberFilter === "inactive") return effectiveUsersList.filter((u: any) => u.status === "inactive")
    return []
  }, [effectiveUsersList, memberFilter])

  const selectedInviteWsCount = React.useMemo(() => {
    return Object.values(inviteWorkspaces).filter((w) => w.enabled).length
  }, [inviteWorkspaces])

  return (
    <>
      <TabsContent value="users" className="outline-none space-y-6">
        {/* CABEÇALHO */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
              <UserCheck className="size-5 text-[#004EB1]" />
              {t.settingsTabUsers || "Gerenciar Membros"}
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
              {t.usersSubtitle || "Gerencie acessos, membros e permissões dos usuários da conta."}
            </p>
          </div>

          {isAdmin && (
            <Button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 gap-1.5 cursor-pointer shadow-sm shrink-0"
            >
              <Plus className="size-4" />
              Convidar Membro
            </Button>
          )}
        </div>

        {/* FILTRO DE ABAS (TODOS / ATIVOS / DESATIVADOS / PENDENTES) */}
        <div className="flex items-center gap-1.5 border-b border-zinc-200 dark:border-zinc-800 pb-2 overflow-x-auto">
          <button
            type="button"
            onClick={() => setMemberFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
              memberFilter === "all"
                ? "bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-xs"
                : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
            }`}
          >
            <Layers className="size-3.5" />
            Todos
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                memberFilter === "all"
                  ? "bg-white/20 dark:bg-black/20 text-white dark:text-zinc-900"
                  : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}
            >
              {effectiveUsersList.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setMemberFilter("active")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
              memberFilter === "active"
                ? "bg-emerald-600 text-white shadow-xs"
                : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
            }`}
          >
            <span className="size-2 rounded-full bg-emerald-400"></span>
            Ativos
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                memberFilter === "active"
                  ? "bg-white/20 text-white"
                  : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}
            >
              {effectiveUsersList.filter((u: any) => (u.status || "active") !== "inactive").length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setMemberFilter("inactive")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
              memberFilter === "inactive"
                ? "bg-zinc-700 text-white shadow-xs"
                : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
            }`}
          >
            <span className="size-2 rounded-full bg-zinc-400"></span>
            Desativados
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                memberFilter === "inactive"
                  ? "bg-white/20 text-white"
                  : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}
            >
              {effectiveUsersList.filter((u: any) => u.status === "inactive").length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setMemberFilter("pending")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
              memberFilter === "pending"
                ? "bg-amber-600 text-white shadow-xs"
                : "text-zinc-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            }`}
          >
            <Mail className="size-3.5" />
            Pendentes
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                memberFilter === "pending"
                  ? "bg-white/20 text-white"
                  : "bg-amber-100 dark:bg-amber-955/40 text-amber-700 dark:text-amber-400"
              }`}
            >
              {pendingInvites.length}
            </span>
          </button>
        </div>

        {/* VISUALIZAÇÃO: PENDENTES */}
        {memberFilter === "pending" && (
          pendingInvites.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl bg-zinc-50/50 dark:bg-zinc-900/30">
              <div className="size-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 mb-3">
                <Inbox className="size-6" />
              </div>
              <h4 className="text-sm font-bold text-zinc-700 dark:text-zinc-300">
                Nenhum convite pendente
              </h4>
              <p className="text-xs text-zinc-400 mt-1 max-w-sm">
                Todos os convites enviados foram aceitos ou não há convites em aberto no momento.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingInvites.map((inv) => {
                const hoursLeft = Math.max(0, Math.round((new Date(inv.expiresAt).getTime() - Date.now()) / (3600 * 1000)))
                const roleLabel = inv.role === "admin" ? "Administrador" : inv.role === "manager" ? "Gerente" : "Membro"

                return (
                  <Card
                    key={inv.id}
                    className="border transition-all shadow-xs border-amber-200/70 dark:border-amber-900/40 bg-amber-50/20 dark:bg-amber-950/10"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="size-10 rounded-xl bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 flex items-center justify-center font-bold text-base shrink-0">
                            <Mail className="size-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white truncate">
                                {inv.email}
                              </CardTitle>
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-955/60 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 flex items-center gap-1">
                                <Clock className="size-3" />
                                Aguardando Aceite
                              </span>
                            </div>
                            <CardDescription className="text-[11px] text-amber-600/90 dark:text-amber-400/90 mt-0.5 truncate">
                              Expira em ~{hoursLeft} horas (Limite de 72h)
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between pt-3 border-t border-amber-100 dark:border-amber-900/20 mt-2 gap-2">
                        <span className="text-[11px] text-zinc-500 dark:text-zinc-400 font-medium">
                          Função: <strong className="text-zinc-700 dark:text-zinc-300 capitalize">{roleLabel}</strong>
                        </span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => toast.success(`Convite reenviado para ${inv.email}`)}
                            className="h-8 text-xs font-semibold text-[#004EB1] border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/30 gap-1.5 cursor-pointer"
                          >
                            Reenviar
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setPendingInvites(prev => prev.filter(i => i.id !== inv.id))}
                            className="h-8 px-2 text-xs font-semibold text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 cursor-pointer"
                            title="Cancelar convite"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )
        )}

        {/* VISUALIZAÇÃO: MEMBROS (CARDS GRID) */}
        {memberFilter !== "pending" && (
          filteredUsersList.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl bg-zinc-50/50 dark:bg-zinc-900/30">
              <div className="size-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 mb-3">
                <Inbox className="size-6" />
              </div>
              <h4 className="text-sm font-bold text-zinc-700 dark:text-zinc-300">
                {memberFilter === "active"
                  ? "Nenhum membro ativo encontrado"
                  : memberFilter === "inactive"
                    ? "Nenhum membro desativado encontrado"
                    : "Nenhum membro encontrado"}
              </h4>
              <p className="text-xs text-zinc-400 mt-1 max-w-sm">
                Não há membros cadastrados nesta conta com os filtros selecionados.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredUsersList.map((usr: any) => {
                const mRole = usr.accountRole || usr.companyRole || usr.role || "member"
                const mStatus = usr.status || "active"
                const isOwner = mRole === "owner"
                const isUserAdmin = mRole === "admin"
                const isManager = mRole === "manager"
                const isInactive = mStatus === "inactive"
                const isSelf = user?.id === (usr.userId || usr.id)
                const canEditThisMember = isAdmin && !isOwner && !isSelf

                return (
                  <Card
                    key={usr.id}
                    className={`border transition-all shadow-xs ${
                      isInactive
                        ? "border-zinc-200 dark:border-zinc-800/80 bg-zinc-50/40 dark:bg-zinc-950/20 opacity-75 hover:opacity-100"
                        : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-700"
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          {usr.avatarUrl ? (
                            <img
                              src={usr.avatarUrl}
                              alt={usr.name || usr.email}
                              className="size-10 rounded-xl object-cover border border-zinc-200 dark:border-zinc-700 shrink-0"
                            />
                          ) : (
                            <div className="size-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 border border-blue-100 dark:border-blue-900/50 flex items-center justify-center font-bold text-base shrink-0">
                              {(usr.name || usr.email || "?")[0].toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white truncate">
                                {usr.name || "Usuário"}
                              </CardTitle>
                              {isSelf && (
                                <span className="text-[10px] bg-blue-100 dark:bg-blue-955 text-[#004EB1] dark:text-blue-300 font-bold px-1.5 py-0.2 rounded-full">
                                  Você
                                </span>
                              )}
                              <span
                                className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border flex items-center gap-1 ${
                                  isOwner
                                    ? "bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-900/50"
                                    : isUserAdmin
                                      ? "bg-blue-50 dark:bg-blue-955/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900/50"
                                      : isManager
                                        ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-900/50"
                                        : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700"
                                }`}
                              >
                                {isOwner ? "Proprietário" : isUserAdmin ? "Administrador" : isManager ? "Gerente" : "Membro"}
                              </span>
                              {isInactive ? (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 flex items-center gap-1">
                                  Desativado
                                </span>
                              ) : (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-955/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/50 flex items-center gap-1">
                                  <span className="size-1.5 rounded-full bg-emerald-500"></span>
                                  Ativo
                                </span>
                              )}
                            </div>
                            <CardDescription className="text-[11px] text-zinc-400 font-mono mt-0.5 truncate">
                              {usr.email}
                            </CardDescription>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between pt-3 border-t border-zinc-100 dark:border-zinc-800 mt-2 gap-2">
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          <MemberWorkspaceCell
                            member={{ userId: usr.userId || usr.id, role: mRole, accountRole: mRole }}
                            allWorkspaces={currentAccount?.workspaces || []}
                            userWorkspacesMap={userWorkspacesMap}
                            fetchUserWorkspaces={fetchUserWorkspaces}
                          />
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {canEditThisMember && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingMember(usr)
                                setEditMemberRole(mRole)
                                setEditMemberStatus(mStatus as "active" | "inactive")
                              }}
                              className="h-8 text-xs font-semibold text-[#004EB1] border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/30 gap-1.5 cursor-pointer"
                            >
                              <Pencil className="size-3.5" />
                              Editar
                            </Button>
                          )}
                          {isSelf && !isOwner && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setMemberToLeave(usr)}
                              className="h-8 px-2 text-xs font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 hover:text-amber-700 cursor-pointer"
                              title="Sair desta empresa"
                            >
                              <LogOut className="size-3.5" />
                            </Button>
                          )}
                          {isAdmin && !isOwner && !isSelf && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setMemberToDelete(usr)}
                              className="h-8 px-2 text-xs font-semibold text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 cursor-pointer"
                              title="Excluir membro da empresa"
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )
        )}
      </TabsContent>

      {/* Modal Convidar Membro */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-zinc-150 dark:border-zinc-800 flex items-center justify-between shrink-0">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <UserCheck className="size-4 text-[#004EB1]" />
                Convidar Novo Membro
              </h3>
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!inviteEmail) return
                const nameToSend = inviteEmail.split("@")[0].trim()

                const selectedWs =
                  inviteRole === "admin"
                    ? []
                    : (currentAccount?.workspaces || [])
                      .filter((ws) => inviteWorkspaces[ws.id]?.enabled)
                      .map((ws) => ({
                        workspaceId: ws.id,
                        role: inviteWorkspaces[ws.id]?.role || "member",
                      }))

                if (inviteRole !== "admin" && selectedWs.length === 0) {
                  toast.error("Selecione pelo menos um workspace para o novo membro.")
                  return
                }

                setIsInviting(true)
                try {
                  await handleInviteUser(inviteEmail, nameToSend, inviteRole, selectedWs)
                  toast.success(`Convite enviado por e-mail para ${inviteEmail}!`)
                  setPendingInvites((prev) => [
                    ...prev,
                    {
                      id: `inv-${Date.now()}`,
                      email: inviteEmail,
                      name: nameToSend,
                      role: inviteRole,
                      invitedAt: new Date().toISOString(),
                      expiresAt: new Date(Date.now() + 72 * 3600 * 1000).toISOString(),
                    },
                  ])
                  setInviteEmail("")
                  setShowInviteModal(false)
                } catch (err: any) {
                  toast.error(err?.message || "Erro ao enviar convite.")
                } finally {
                  setIsInviting(false)
                }
              }}
              className="p-5 space-y-4 overflow-y-auto flex-1"
            >
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">E-mail</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="carlos@suaempresa.com.br"
                  className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none focus:border-[#004EB1]"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">Função na Empresa</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none cursor-pointer"
                >
                  <option value="member">Membro</option>
                  <option value="manager">Gerente</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {/* Seção de Workspaces */}
              {inviteRole === "admin" ? (
                <div className="p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-955/20 border border-blue-200 dark:border-blue-900/40 space-y-1 text-xs">
                  <div className="flex items-center gap-2 font-bold text-[#004EB1] dark:text-blue-300">
                    <Shield className="size-4" />
                    Acesso Universal de Administrador
                  </div>
                  <p className="text-[11px] text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    Como Administrador da Conta, este usuário terá acesso total e privilégios de administrador em <strong>todos os workspaces</strong> da organização automaticamente.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 pt-1 border-t border-zinc-150 dark:border-zinc-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300">
                        Workspaces & Permissões
                      </label>
                      <p className="text-[10px] text-zinc-500">
                        Selecione pelo menos 1 workspace que este membro poderá acessar:
                      </p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${selectedInviteWsCount > 0
                      ? "bg-blue-50 text-[#004EB1] dark:bg-blue-955/40 dark:text-blue-300"
                      : "bg-red-100 text-red-700 dark:bg-red-955/40 dark:text-red-400"
                      }`}>
                      {selectedInviteWsCount} selecionado(s)
                    </span>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1 border border-zinc-200 dark:border-zinc-800 rounded-lg p-2 bg-zinc-50/50 dark:bg-zinc-950/40 scrollbar-thin">
                    {(currentAccount?.workspaces || []).length === 0 ? (
                      <div className="p-3 text-center text-xs text-zinc-400">Nenhum workspace encontrado nesta conta.</div>
                    ) : (
                      (currentAccount?.workspaces || []).map((ws) => {
                        const isSelected = inviteWorkspaces[ws.id]?.enabled ?? true
                        const currentWsRole = inviteWorkspaces[ws.id]?.role || "member"

                        return (
                          <div
                            key={ws.id}
                            className={`p-2 rounded-md border transition-all flex items-center justify-between gap-3 ${isSelected
                              ? "bg-white dark:bg-zinc-900 border-zinc-250 dark:border-zinc-700 shadow-2xs"
                              : "bg-transparent border-transparent opacity-60"
                              }`}
                          >
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const checked = e.target.checked
                                  setInviteWorkspaces((prev) => ({
                                    ...prev,
                                    [ws.id]: {
                                      enabled: checked,
                                      role: prev[ws.id]?.role || "member",
                                    },
                                  }))
                                }}
                                className="rounded border-zinc-300 text-[#004EB1] focus:ring-[#004EB1] size-3.5 cursor-pointer"
                              />
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-zinc-900 dark:text-white truncate">
                                  {ws.name}
                                </div>
                                <div className="text-[10px] text-zinc-400 truncate">/{ws.slug}</div>
                              </div>
                            </label>

                            <select
                              value={currentWsRole}
                              disabled={!isSelected}
                              onChange={(e) => {
                                const newRole = e.target.value
                                setInviteWorkspaces((prev) => ({
                                  ...prev,
                                  [ws.id]: {
                                    enabled: prev[ws.id]?.enabled ?? true,
                                    role: newRole,
                                  },
                                }))
                              }}
                              className={`rounded border px-2 py-1 text-[11px] bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none cursor-pointer ${isSelected
                                ? "border-zinc-200 dark:border-zinc-800"
                                : "border-transparent bg-transparent opacity-40 cursor-not-allowed"
                                }`}
                            >
                              <option value="member">Membro</option>
                              <option value="manager">Gerente</option>
                              <option value="admin">Administrador</option>
                            </select>
                          </div>
                        )
                      })
                    )}
                  </div>
                  {selectedInviteWsCount === 0 && (
                    <p className="text-[11px] font-semibold text-red-500 dark:text-red-400 flex items-center gap-1">
                      <AlertTriangle className="size-3.5" />
                      É obrigatório selecionar pelo menos 1 workspace para membros regulares.
                    </p>
                  )}
                </div>
              )}

              <div className="p-3 rounded-lg bg-amber-50/60 dark:bg-amber-955/20 border border-amber-200 dark:border-amber-900/30 text-[11px] text-amber-800 dark:text-amber-300 font-sans">
                ⏱️ <strong>Limite de Validade:</strong> O convite terá validade máxima de <strong>72 horas</strong> a partir do envio por e-mail.
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isInviting}
                  onClick={() => setShowInviteModal(false)}
                  className="text-xs h-8 font-sans cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isInviting || (inviteRole !== "admin" && selectedInviteWsCount === 0)}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-4 font-sans cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isInviting ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar Convite"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Membro */}
      {editingMember && (() => {
        const isEditingOwner = (editingMember.accountRole || editingMember.companyRole || editingMember.role || editMemberRole) === "owner"

        return (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
              <div className="p-4 border-b border-zinc-150 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                  <Pencil className="size-4 text-[#004EB1]" />
                  {isEditingOwner ? "Editar Proprietário" : "Editar Dados do Membro"}
                </h3>
                <button
                  type="button"
                  onClick={() => setEditingMember(null)}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm font-bold cursor-pointer"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 dark:bg-zinc-950/50 border border-zinc-200 dark:border-zinc-800">
                  {editingMember.avatarUrl ? (
                    <img
                      src={editingMember.avatarUrl}
                      alt={editingMember.name || editingMember.email}
                      className="size-10 rounded-xl object-cover border border-zinc-200 dark:border-zinc-700 shrink-0"
                    />
                  ) : (
                    <div className="size-10 rounded-xl bg-blue-50 dark:bg-blue-955/40 text-[#004EB1] dark:text-blue-400 border border-blue-100 dark:border-blue-900/50 flex items-center justify-center font-bold text-sm shrink-0">
                      {(editingMember.name || editingMember.email || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-zinc-900 dark:text-white truncate">
                      {editingMember.name || "Usuário"}
                    </p>
                    <p className="text-[11px] text-zinc-400 font-mono truncate">
                      {editingMember.email}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">Função</label>
                    <select
                      value={editMemberRole}
                      disabled={isEditingOwner}
                      onChange={(e) => setEditMemberRole(e.target.value)}
                      className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isEditingOwner ? (
                        <option value="owner">Proprietário</option>
                      ) : (
                        <>
                          <option value="member">Membro</option>
                          <option value="manager">Gerente</option>
                          <option value="admin">Administrador</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">Status</label>
                    <div className="flex items-center justify-between h-[34px] px-3 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
                      <span className={`text-xs font-semibold ${editMemberStatus === "active" ? "text-emerald-600 dark:text-emerald-400" : "text-zinc-400 dark:text-zinc-500"}`}>
                        {editMemberStatus === "active" ? "Ativo" : "Desativado"}
                      </span>
                      <button
                        type="button"
                        disabled={isEditingOwner}
                        onClick={() => setEditMemberStatus((prev) => (prev === "active" ? "inactive" : "active"))}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${editMemberStatus === "active" ? "bg-[#004EB1]" : "bg-zinc-200 dark:bg-zinc-800"
                          }`}
                      >
                        <span
                          className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${editMemberStatus === "active" ? "translate-x-4" : "translate-x-0"
                            }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Seção Workspaces Vinculados */}
                <div className="pt-2 border-t border-zinc-150 dark:border-zinc-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5">
                      <FolderOpen className="size-3.5 text-[#004EB1]" />
                      Workspaces Vinculados
                    </label>
                  </div>

                  {editMemberRole === "admin" || editMemberRole === "owner" ? (
                    <div className="p-2.5 rounded-lg bg-blue-50/60 dark:bg-blue-955/20 border border-blue-200 dark:border-blue-900/40 text-[11px] text-[#004EB1] dark:text-blue-300 font-medium">
                      🛡️ Como {editMemberRole === "owner" ? "Proprietário" : "Administrador"}, o usuário tem acesso total a todos os workspaces desta empresa.
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-950/40 space-y-2">
                      {(() => {
                        const memberId = editingMember.userId || editingMember.id
                        const assignedIds = userWorkspacesMap[memberId] || []
                        const assigned = (currentAccount?.workspaces || []).filter((ws) => assignedIds.includes(ws.id))

                        if (assigned.length === 0) {
                          return (
                            <div className="text-[11px] text-zinc-500 italic">
                              Acesso padrão ou nenhum workspace atribuído individualmente.
                            </div>
                          )
                        }

                        return (
                          <div className="flex flex-wrap gap-1.5">
                            {assigned.map((ws) => (
                              <span
                                key={ws.id}
                                className="bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-200 text-[10px] font-semibold px-2 py-0.5 rounded-md border border-zinc-200 dark:border-zinc-700 flex items-center gap-1 shadow-2xs"
                              >
                                <FolderOpen className="size-2.5 text-[#004EB1]" />
                                {ws.name}
                              </span>
                            ))}
                          </div>
                        )
                      })()}
                      <p className="text-[10px] text-zinc-400 pt-1">
                        💡 A associação e desassociação de workspaces para este membro pode ser configurada diretamente na aba de Workspaces.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-zinc-150 dark:border-zinc-800 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSavingMember}
                  onClick={() => setEditingMember(null)}
                  className="text-xs h-8 font-sans cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  disabled={isSavingMember}
                  onClick={async () => {
                    setIsSavingMember(true)
                    try {
                      const memberId = editingMember.userId || editingMember.id

                      if (handleUpdateAccountMember) {
                        await handleUpdateAccountMember(memberId, {
                          name: editingMember.name,
                          role: isEditingOwner ? "owner" : editMemberRole,
                          status: isEditingOwner ? "active" : editMemberStatus,
                        })
                      } else if (handleUpdateUserRole) {
                        handleUpdateUserRole(memberId, isEditingOwner ? "owner" : editMemberRole)
                      }

                      toast.success(`Dados do membro ${editingMember.name || editingMember.email} salvos com sucesso!`)
                      setEditingMember(null)
                    } catch (err: any) {
                      toast.error(err?.message || "Erro ao salvar dados do membro.")
                    } finally {
                      setIsSavingMember(false)
                    }
                  }}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-4 font-sans cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSavingMember ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    "Salvar Alterações"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Modal Confirmar Exclusão de Membro */}
      {memberToDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto shadow-xs">
              <AlertTriangle className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Excluir Membro da Empresa
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Tem certeza de que deseja remover <strong>{memberToDelete.name || memberToDelete.email}</strong>?
                <br />
                O usuário perderá o acesso a esta conta corporativa e a todos os seus workspaces.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isDeletingMember}
                onClick={() => setMemberToDelete(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isDeletingMember || !isAdmin}
                onClick={async () => {
                  if (!isAdmin) {
                    toast.error("Apenas o Proprietário e os Administradores da empresa podem remover membros.")
                    return
                  }
                  setIsDeletingMember(true)
                  try {
                    const memberId = memberToDelete.userId || memberToDelete.id
                    await handleRemoveUser(memberId)
                    toast.success(`Membro ${memberToDelete.name || memberToDelete.email} excluído com sucesso!`)
                    setMemberToDelete(null)
                  } catch (err: any) {
                    toast.error(err?.message || "Erro ao remover membro.")
                  } finally {
                    setIsDeletingMember(false)
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-4 font-sans cursor-pointer flex-1 flex items-center justify-center gap-1.5"
              >
                {isDeletingMember ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  "Excluir Membro"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Saída da Empresa */}
      {memberToLeave && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto shadow-xs">
              <LogOut className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Sair da Empresa
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Tem certeza de que deseja sair de <strong>{currentAccount?.name || "esta empresa"}</strong>?
                <br />
                Você perderá o acesso a esta conta corporativa e a todos os seus workspaces.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isLeavingMember}
                onClick={() => setMemberToLeave(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isLeavingMember}
                onClick={async () => {
                  setIsLeavingMember(true)
                  try {
                    const memberId = memberToLeave.userId || memberToLeave.id
                    await handleRemoveUser(memberId)
                    toast.success(`Você saiu da empresa com sucesso.`)
                    setMemberToLeave(null)
                  } catch (err: any) {
                    toast.error(err?.message || "Erro ao sair da empresa.")
                  } finally {
                    setIsLeavingMember(false)
                  }
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 px-4 font-sans cursor-pointer flex-1 flex items-center justify-center gap-1.5"
              >
                {isLeavingMember ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Saindo...
                  </>
                ) : (
                  "Confirmar e Sair"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
